/**
 * 답 하나(일반 문항 하나, GR 지점 하나)의 저장 상태와 요청 규칙. 디자인 v0.3 상태표 4장·5장·6장.
 *
 * - "저장됨"은 서버 200이 돌려준 수정 번호의 값이 지금 화면 값과 같을 때만(원칙 1).
 * - 화면 값은 학생이 바꾸기 전까지 지우지 않는다(원칙 2).
 * - 대상당 동시 요청 1개. 요청 중 바뀐 값은 마지막 값만 기다렸다가 이어서 보낸다.
 * - 응답 없음·네트워크 오류는 같은 요청 키·같은 내용으로 다시 보낸다(1초, 2초, 4초 … 최대 30초). 새 값을 먼저 보내지 않는다.
 * - 409 stale_revision은 자동 재저장하지 않고 확인(충돌) 상태로 둔다.
 * - 409 request_key_reused는 새 키를 자동으로 만들지 않고 그 대상의 자동 저장을 멈춘다(4-3).
 * - 401은 모든 저장을 멈추고 세대 번호를 올린다. 응답을 기다리던 요청은 "결과 미확정"으로 남기고, 늦은 응답은 반영하지 않는다.
 */
import { devlog } from './devlog.js';
import { bumpGen, currentGen, type ApiResult, type Transport } from './transport.js';
import { grIdsOf, grValueFromSaved, isEmptyValue, sameValue, type TargetValue } from './types.js';

export type TargetKind = 'gr' | 'choice';
export type DisplayStatus = 'empty' | 'dirty' | 'saving' | 'saved' | 'failed' | 'conflict' | 'locked';

export interface Sent { readonly key: string; readonly value: TargetValue; readonly base: number }
export interface ServerValue { readonly value: TargetValue; readonly revision: number }

export interface Target {
  readonly id: string;
  readonly kind: TargetKind;
  screen: TargetValue;
  confirmed: ServerValue;
  inflight: Sent | null;
  /** 결과를 아직 모르는 요청(응답 없음, 401로 중단). 같은 키·같은 내용으로만 다시 보낸다. */
  unresolved: Sent | null;
  /** 자동 저장을 멈춘 이유 */
  flag: null | 'conflict' | 'keyconflict' | 'error';
  /** 충돌 확인에서 학생에게 보여 준 서버 값과 수정 번호("학생이 본 버전") */
  seen: ServerValue | null;
  /** 마지막 200 응답의 상태(일반 문항: answered·unanswered·input-error). OMR 입력 오류 안내용 */
  lastState: string | null;
  retryIdx: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  debounceTimer: ReturnType<typeof setTimeout> | null;
}

export type SaveEvent =
  | { readonly type: 'changed'; readonly id: string }
  | { readonly type: 'network'; readonly id: string }
  | { readonly type: 'conflict'; readonly id: string }
  | { readonly type: 'keyconflict'; readonly id: string }
  | { readonly type: 'unauthorized' }
  | { readonly type: 'forbidden'; readonly code: string }
  | { readonly type: 'attempt-closed'; readonly status: 'submitted' | 'void' }
  | { readonly type: 'server-error'; readonly id: string };

export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000] as const;
export const DEBOUNCE_MS = 600;

export type ResolveOutcome =
  | { readonly kind: 'saved' }
  | { readonly kind: 'stale'; readonly server: ServerValue | null }
  | { readonly kind: 'network' }
  | { readonly kind: 'closed'; readonly status: 'submitted' | 'void' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'failed'; readonly code: string };

export interface SaveManagerDeps {
  readonly transport: Transport;
  readonly attemptId: string;
  readonly deviceId: string;
  readonly newKey: () => string;
  readonly emit: (e: SaveEvent) => void;
  readonly debounceMs?: number;
}

/** 서버 409의 current(또는 조회 값)를 화면 값 형식으로 */
export function serverValueFromCurrent(kind: TargetKind, current: any): ServerValue | null {
  if (current === null || current === undefined || typeof current !== 'object') return null;
  if (kind === 'gr') return { value: grValueFromSaved(current), revision: Number(current.revision ?? 0) };
  return { value: choiceFromSaved(current.state, current.value, current.omrRaw ?? null, current.channel ?? 'screen'), revision: Number(current.revision ?? 0) };
}

/** 서버 저장 값 → 화면 값. OMR 경로로 저장된 값은 원본(raw) 그대로 OMR 값으로 둔다. */
export function choiceFromSaved(state: string, value: number | null, omrRaw: string | null, channel: string = 'screen'): TargetValue {
  if (channel === 'omr') return { kind: 'omr', raw: omrRaw ?? '' };
  if (state === 'answered' && value !== null) return { kind: 'choice', value };
  if (state === 'unanswered') return { kind: 'blank' };
  return { kind: 'other', state, raw: omrRaw };
}

export class SaveManager {
  private readonly targets = new Map<string, Target>();
  private paused = false;
  private locked = false;
  private disposed = false;
  private waiters: (() => void)[] = [];

  constructor(private readonly d: SaveManagerDeps) {}

  add(id: string, kind: TargetKind, server: ServerValue): void {
    this.targets.set(id, {
      id, kind, screen: server.value, confirmed: server, inflight: null, unresolved: null, flag: null, seen: null, lastState: null,
      retryIdx: 0, retryTimer: null, debounceTimer: null,
    });
  }

  get(id: string): Target {
    const t = this.targets.get(id);
    if (t === undefined) throw new Error(`unknown target ${id}`);
    return t;
  }
  all(): Target[] { return [...this.targets.values()]; }
  isPaused(): boolean { return this.paused; }
  isLocked(): boolean { return this.locked; }

  status(id: string): DisplayStatus {
    const t = this.get(id);
    if (this.locked) return 'locked';
    if (t.flag !== null && t.flag !== 'error') return 'conflict';
    if (t.inflight !== null) return 'saving';
    if (t.unresolved !== null || t.flag === 'error') return 'failed';
    if (!sameValue(t.screen, t.confirmed.value)) return 'dirty';
    return isEmptyValue(t.screen) ? 'empty' : 'saved';
  }

  counts(): Record<DisplayStatus, number> {
    const c: Record<DisplayStatus, number> = { empty: 0, dirty: 0, saving: 0, saved: 0, failed: 0, conflict: 0, locked: 0 };
    for (const t of this.targets.values()) c[this.status(t.id)]++;
    return c;
  }

  /** 저장 전·저장 중이 없는지 */
  settled(): boolean {
    const c = this.counts();
    return c.dirty === 0 && c.saving === 0;
  }

  /** 학생이 값을 바꿈. 같은 값을 다시 고르면 아무 변화 없음. */
  setValue(id: string, v: TargetValue): void {
    const t = this.get(id);
    if (this.locked || this.disposed) return;
    if (sameValue(t.screen, v)) return;
    t.screen = v;
    if (t.debounceTimer !== null) clearTimeout(t.debounceTimer);
    if (t.flag === null) t.debounceTimer = setTimeout(() => { t.debounceTimer = null; this.sendNext(t); }, this.d.debounceMs ?? DEBOUNCE_MS);
    this.changed(t);
  }

  /** 기다리지 않고 바로 보낸다(제출 전 확인). */
  flushAll(): void {
    for (const t of this.targets.values()) {
      if (t.debounceTimer !== null) { clearTimeout(t.debounceTimer); t.debounceTimer = null; }
      this.sendNext(t);
    }
  }

  /** "다시 시도": 결과 미확정 요청을 같은 키로 바로 다시 보낸다. */
  retryNow(): void {
    for (const t of this.targets.values()) {
      if (t.retryTimer !== null) { clearTimeout(t.retryTimer); t.retryTimer = null; }
      if (t.flag === 'error') t.flag = null;
      this.sendNext(t);
    }
  }

  /** 저장 전·저장 중이 없어질 때까지(최대 timeoutMs). 결과: 기다린 끝에 모두 처리됐는지 */
  async whenSettled(timeoutMs: number): Promise<boolean> {
    if (this.settled()) return true;
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => { this.waiters = this.waiters.filter((w) => w !== check); resolve(this.settled()); }, timeoutMs);
      const check = () => {
        if (!this.settled()) return;
        clearTimeout(timer);
        this.waiters = this.waiters.filter((w) => w !== check);
        resolve(true);
      };
      this.waiters.push(check);
    });
  }

  /** 제출 확인 중(S-7)·제출 후(S-6). 화면 값은 그대로 둔다. */
  lock(): void {
    this.locked = true;
    for (const t of this.targets.values()) this.clearTimers(t);
    for (const t of this.targets.values()) this.changed(t);
  }
  unlock(): void {
    this.locked = false;
    for (const t of this.targets.values()) { this.changed(t); this.sendNext(t); }
  }

  /** 계정 전환·나가기(L-3): 타이머·대기 요청 정리. 서버 기록은 지우지 않는다. */
  dispose(): void {
    this.disposed = true;
    for (const t of this.targets.values()) this.clearTimers(t);
    this.targets.clear();
    this.waiters = [];
  }

  /** 401 뒤 같은 학생으로 다시 로그인한 다음, 대조를 마치고 저장을 다시 시작할 때 */
  resume(): void {
    this.paused = false;
    for (const t of this.targets.values()) this.sendNext(t);
  }

  // ---------------------------------------------------------------- 요청

  private path(t: Target): string {
    return t.kind === 'gr'
      ? `/attempts/${this.d.attemptId}/gr-points/${t.id}`
      : `/attempts/${this.d.attemptId}/responses/${t.id}`;
  }

  private bodyOf(t: Target, value: TargetValue, base: number): unknown {
    if (t.kind === 'gr') return { ...(grIdsOf(value as string | null).length > 0 ? { selectedOptionIds: grIdsOf(value as string | null) } : { selectedOptionId: null }), baseRevision: base, deviceId: this.d.deviceId };
    const v = value as Exclude<TargetValue, string | null>;
    if (v.kind === 'omr') return { channel: 'omr', omrRaw: v.raw, baseRevision: base, deviceId: this.d.deviceId };
    const input = v.kind === 'choice' ? { kind: 'choice', value: v.value } : { kind: 'blank' };
    return { channel: 'screen', input, baseRevision: base, deviceId: this.d.deviceId };
  }

  private sendNext(t: Target): void {
    if (this.paused || this.locked || this.disposed || t.inflight !== null || t.flag !== null) return;
    if (t.retryTimer !== null) return; // 재시도 대기 중이면 그 순서를 따른다
    if (t.unresolved !== null) { void this.send(t, t.unresolved); return; }
    if (sameValue(t.screen, t.confirmed.value)) return;
    if (t.kind === 'choice' && typeof t.screen === 'object' && t.screen !== null && t.screen.kind === 'other') return;
    void this.send(t, { key: this.d.newKey(), value: t.screen, base: t.confirmed.revision });
  }

  private async send(t: Target, sent: Sent): Promise<void> {
    t.inflight = sent;
    this.changed(t);
    const r = await this.d.transport.request('PUT', this.path(t), { key: sent.key, body: this.bodyOf(t, sent.value, sent.base) });
    // 세대가 바뀐 뒤(계정 전환·재로그인 시작) 도착한 응답은 화면에 반영하지 않는다.
    if (this.disposed || r.gen !== currentGen()) return;
    if (t.inflight === sent) t.inflight = null;
    this.handle(t, sent, r);
    this.changed(t);
  }

  private handle(t: Target, sent: Sent, r: ApiResult): void {
    if (r.kind === 'network' || (r.kind === 'http' && r.status >= 500)) {
      if (r.kind === 'http') devlog('save', `http_${r.status}:${String(r.body['error'] ?? '')}`, sent.key);
      t.unresolved = sent;
      this.scheduleRetry(t);
      this.d.emit({ type: 'network', id: t.id });
      return;
    }
    const code = String(r.body['error'] ?? '');
    if (r.status === 200 && r.body['saved'] === true) {
      t.unresolved = null;
      t.retryIdx = 0;
      const rev = Number(r.body['revision']);
      // 확인 수정 번호는 더 낮은 값(오래된 응답의 번호)으로 덮어쓰지 않는다.
      if (rev >= t.confirmed.revision) { t.confirmed = { value: sent.value, revision: rev }; t.lastState = typeof r.body['state'] === 'string' ? r.body['state'] : null; }
      if (!sameValue(t.screen, t.confirmed.value)) this.sendNext(t);
      return;
    }
    if (r.status === 409 && code === 'stale_revision') {
      t.unresolved = null;
      t.flag = 'conflict';
      t.seen = serverValueFromCurrent(t.kind, r.body['current']);
      this.d.emit({ type: 'conflict', id: t.id });
      return;
    }
    if (r.status === 409 && code === 'request_key_reused') {
      t.unresolved = null;
      t.flag = 'keyconflict';
      t.seen = null;
      devlog('save', code, sent.key);
      this.d.emit({ type: 'keyconflict', id: t.id });
      return;
    }
    if (r.status === 409 && (code === 'already_submitted' || code === 'attempt_void')) {
      t.unresolved = null;
      this.d.emit({ type: 'attempt-closed', status: code === 'already_submitted' ? 'submitted' : 'void' });
      return;
    }
    if (r.status === 401) {
      t.unresolved = sent;
      this.onUnauthorized();
      return;
    }
    if (r.status === 403) {
      t.unresolved = sent;
      this.pauseAll();
      this.d.emit({ type: 'forbidden', code });
      return;
    }
    // 400·404·그 밖: 자동 재시도하지 않는다. 학생에게는 일반 문구, 코드는 개발 로그에만.
    devlog('save', `http_${r.status}:${code}`, sent.key);
    t.unresolved = null;
    t.flag = 'error';
    this.d.emit({ type: 'server-error', id: t.id });
  }

  /** 401: 모든 저장 중지, 응답 대기 요청은 결과 미확정, 세대 번호 올림 */
  onUnauthorized(): void {
    if (this.paused) return;
    this.pauseAll();
    bumpGen();
    this.d.emit({ type: 'unauthorized' });
  }

  private pauseAll(): void {
    this.paused = true;
    for (const t of this.targets.values()) {
      this.clearTimers(t);
      if (t.inflight !== null) { t.unresolved = t.inflight; t.inflight = null; }
      this.changed(t);
    }
  }

  private scheduleRetry(t: Target): void {
    if (this.paused || this.locked || this.disposed) return;
    const delay = RETRY_DELAYS_MS[Math.min(t.retryIdx, RETRY_DELAYS_MS.length - 1)]!;
    t.retryIdx++;
    t.retryTimer = setTimeout(() => { t.retryTimer = null; this.sendNext(t); }, delay);
  }

  private clearTimers(t: Target): void {
    if (t.retryTimer !== null) { clearTimeout(t.retryTimer); t.retryTimer = null; }
    if (t.debounceTimer !== null) { clearTimeout(t.debounceTimer); t.debounceTimer = null; }
  }

  private changed(t: Target): void {
    this.d.emit({ type: 'changed', id: t.id });
    for (const w of [...this.waiters]) w();
  }

  // ---------------------------------------------------------------- 충돌 해결(6장)·재로그인 대조(5장)

  /** C-1·K-2: 학생에게 보여 준 서버 값과 수정 번호를 기억한다. */
  setSeen(id: string, seen: ServerValue): void {
    const t = this.get(id);
    t.seen = seen;
    this.changed(t);
  }

  /** C-2: 이 화면의 답으로 저장. 학생이 본 수정 번호로 조건부 저장. 같은 결정을 다시 누르면 같은 키를 쓴다. */
  async keepScreen(id: string, decisionKey: string): Promise<ResolveOutcome> {
    const t = this.get(id);
    if (t.seen === null) return { kind: 'failed', code: 'not_seen' };
    const sent: Sent = { key: decisionKey, value: t.screen, base: t.seen.revision };
    t.inflight = sent;
    this.changed(t);
    const r = await this.d.transport.request('PUT', this.path(t), { key: sent.key, body: this.bodyOf(t, sent.value, sent.base) });
    if (this.disposed || r.gen !== currentGen()) return { kind: 'failed', code: 'stale_generation' };
    t.inflight = null;
    let out: ResolveOutcome;
    if (r.kind === 'network' || r.status >= 500) {
      out = { kind: 'network' };
    } else {
      const code = String(r.body['error'] ?? '');
      if (r.status === 200 && r.body['saved'] === true) {
        t.confirmed = { value: sent.value, revision: Number(r.body['revision']) };
        t.flag = null; t.seen = null; t.unresolved = null;
        out = { kind: 'saved' };
        if (!sameValue(t.screen, t.confirmed.value)) this.sendNext(t);
      } else if (r.status === 409 && code === 'stale_revision') {
        // C-2b: 자동으로 다시 저장하지 않는다. 바뀐 서버 값·번호를 기억한 번호로 바꾼다.
        const s = serverValueFromCurrent(t.kind, r.body['current']);
        if (s !== null) t.seen = s;
        out = { kind: 'stale', server: s };
      } else if (r.status === 409 && (code === 'already_submitted' || code === 'attempt_void')) {
        out = { kind: 'closed', status: code === 'already_submitted' ? 'submitted' : 'void' };
      } else if (r.status === 401) {
        this.onUnauthorized();
        out = { kind: 'unauthorized' };
      } else {
        devlog('save', `resolve_http_${r.status}:${code}`, sent.key);
        out = { kind: 'failed', code };
      }
    }
    this.changed(t);
    return out;
  }

  /** C-3·R-7: 서버 값을 화면 값으로 받아들이고 저장됨으로 둔다. */
  takeServer(id: string, server: ServerValue): void {
    const t = this.get(id);
    this.clearTimers(t);
    t.screen = server.value;
    t.confirmed = server;
    t.flag = null; t.seen = null; t.unresolved = null;
    this.changed(t);
  }

  /** R-5: 결과 미확정 요청을 같은 키·같은 내용으로 다시 보내 이전 요청의 결과만 얻는다(화면 상태는 바꾸지 않음). */
  async replayUnresolved(id: string): Promise<'none' | 'network' | { ok: true; revision: number; value: TargetValue } | { ok: false; code: string; status: number }> {
    const t = this.get(id);
    const sent = t.unresolved;
    if (sent === null) return 'none';
    const r = await this.d.transport.request('PUT', this.path(t), { key: sent.key, body: this.bodyOf(t, sent.value, sent.base) });
    if (r.kind === 'network') return 'network';
    if (r.status === 200 && r.body['saved'] === true) return { ok: true, revision: Number(r.body['revision']), value: sent.value };
    if (r.status === 409 && String(r.body['error']) === 'request_key_reused') devlog('save', 'request_key_reused', sent.key, 'replay');
    return { ok: false, code: String(r.body['error'] ?? ''), status: r.status };
  }

  /** R-7 결과 적용 */
  applyReconcile(id: string, d: ReconcileDecision): void {
    const t = this.get(id);
    t.unresolved = null;
    t.inflight = null;
    if (d.action === 'saved') { t.confirmed = d.confirmed; t.flag = null; }
    else if (d.action === 'continue') { t.confirmed = d.confirmed; t.flag = null; }
    else if (d.action === 'conflict') { t.flag = 'conflict'; t.seen = d.server; }
    else { t.flag = 'keyconflict'; t.seen = d.server; }
    this.changed(t);
  }
}

// ---------------------------------------------------------------- R-7 대조 규칙(순수 함수)

export type PrevResult = { readonly kind: 'ok'; readonly value: TargetValue; readonly revision: number } | { readonly kind: 'keyreused' } | { readonly kind: 'none' };

export type ReconcileDecision =
  | { readonly action: 'saved'; readonly confirmed: ServerValue }
  | { readonly action: 'continue'; readonly confirmed: ServerValue }
  | { readonly action: 'conflict'; readonly server: ServerValue }
  | { readonly action: 'keyconflict'; readonly server: ServerValue };

/**
 * 한 대상의 ① 이전 요청 결과 ② 최신 서버 값 ③ 지금 화면 값 대조(상태표 5장 R-7).
 * - 최신 서버 값 = 화면 값 → 저장됨
 * - 최신 번호·값 = 알고 있던 값(이전 요청이 200이면 그 값·번호, 아니면 마지막 확인 값·번호) → 그 번호로 이어서 저장
 * - 그 밖 → 충돌 확인. 이전 요청의 200만 보고 "저장됨"으로 표시하지 않는다.
 * - 이전 요청이 request_key_reused → 4-3
 */
export function reconcileTarget(screen: TargetValue, confirmed: ServerValue, prev: PrevResult, server: ServerValue): ReconcileDecision {
  if (prev.kind === 'keyreused') return { action: 'keyconflict', server };
  if (sameValue(server.value, screen)) return { action: 'saved', confirmed: server };
  const known = prev.kind === 'ok' ? { value: prev.value, revision: prev.revision } : confirmed;
  if (server.revision === known.revision && sameValue(server.value, known.value)) return { action: 'continue', confirmed: server };
  return { action: 'conflict', server };
}
